/**
 * @module pages/CardPage
 * @description One story card at full size with its back, the icon
 * toolbar (print, image, share) and the call to give, carrying source
 * attribution (?from=<slug>).
 */
import { useRef } from "react";
import { StoryPostcard } from "../components/StoryPostcard";
import { StoryCardBack } from "../components/CardBacks";
import { OutputBar } from "../components/OutputBar";
import { ChallengePanel } from "../components/MatchMeter";
import { usePublishedCards, useSettings } from "../data";
import { hrefFor, shareUrlForCard } from "../app/router";
import { useEdition } from "../app/edition";
import { CAMPAIGN } from "../config/campaign";
import { PRINT_SIZES } from "../config/print";
import { usePrint } from "../lib/print/PrintService";

export function CardPage({ slug }: { slug: string }) {
    const cards = usePublishedCards();
    const edition = useEdition();
    const { printSize } = useSettings();
    const print = usePrint();
    const ref = useRef<HTMLElement>(null);
    const index = cards.findIndex((c) => c.slug === slug);
    const card = cards[index];

    if (!card) {
        return (
            <section className="page-narrow">
                <h1 className="section-title">That postcard isn't available.</h1>
                <a className="btn" href={hrefFor({ name: "home" })}>Back to all stories</a>
            </section>
        );
    }

    const prev = cards[(index - 1 + cards.length) % cards.length];
    const next = cards[(index + 1) % cards.length];
    const payload = {
        title: `${card.title} — ${CAMPAIGN.campaignName}`,
        text: [
            `${card.statValue.replace(/\n/g, " ")} ${card.statLabel.toLowerCase()}.`,
            `${card.title}, supported by ${CAMPAIGN.organizationName}.`,
            edition.card.shareSuffix,
        ]
            .filter(Boolean)
            .join(" "),
        url: shareUrlForCard(card.slug),
    };

    /** Front and back, one per sheet, at the device's default postcard size. */
    const printCard = () =>
        print({
            size: PRINT_SIZES[printSize],
            pages: [
                { key: "front", node: <StoryPostcard card={card} edition={edition} /> },
                { key: "back", node: <StoryCardBack card={card} edition={edition} /> },
            ],
        });

    return (
        <section className="card-page">
            <nav className="card-pager" aria-label="Postcards">
                <a href={hrefFor({ name: "card", slug: prev.slug })}>← {prev.title}</a>
                <span>{index + 1} / {cards.length}</span>
                <a href={hrefFor({ name: "card", slug: next.slug })}>{next.title} →</a>
            </nav>
            <StoryPostcard ref={ref} card={card} className="postcard--feature" />
            <OutputBar targetRef={ref} payload={payload} filename={`afj-${card.slug}.png`} onPrint={printCard} />
            <div className="card-cta">
                <ChallengePanel variant="compact" />
                <a className="btn btn--primary btn--lg" href={hrefFor({ name: "pledge", fromSlug: card.slug })}>
                    {edition.card.ctaLabel}
                </a>
            </div>
            <details className="card-back-preview">
                <summary>See the back of this postcard</summary>
                <StoryCardBack card={card} />
            </details>
        </section>
    );
}
